import * as React from 'react';

import { SyncTaskStart, SyncTaskState, SyncTaskStatus, useSyncEmployeePollingQuery } from 'services/busmanApi';

import { getPercent, isTerminalState, POLL_INTERVAL_MS } from './syncTask';

// Every MYOB status endpoint shares the same signature, so one of them types them all
type UseSyncPollingQuery = typeof useSyncEmployeePollingQuery;

interface UseMyobTaskSyncArgs {
  // Used to build the log lines, e.g. "Syncing Employees..."
  label: string;
  successMessage: string;
  errorMessage: string;
  startSync: () => Promise<{ data?: SyncTaskStart; error?: unknown }>;
  usePollingQuery: UseSyncPollingQuery;
  onLog: (type: string, message: string) => void;
  onSettled: (type: 'success' | 'error', message: string) => void;
}

export interface MyobTaskSync {
  start: () => Promise<void>;
  isSyncing: boolean;
  percent: number;
  state?: SyncTaskState;
  message?: string;
}

/**
 * Couples a MYOB sync endpoint with its status endpoint: the sync call returns a
 * celery task id, which is then polled until the task reaches a terminal state.
 */
export function useMyobTaskSync({
  label,
  successMessage,
  errorMessage,
  startSync,
  usePollingQuery,
  onLog,
  onSettled,
}: UseMyobTaskSyncArgs): MyobTaskSync {
  const [taskId, setTaskId] = React.useState<string | null>(null);
  const [isStarting, setIsStarting] = React.useState(false);
  // The polling cache entry is dropped as soon as we unsubscribe, so keep the last frame for display
  const [lastStatus, setLastStatus] = React.useState<SyncTaskStatus | null>(null);

  const { data: status, error: pollError } = usePollingQuery(taskId as string, {
    skip: !taskId,
    pollingInterval: POLL_INTERVAL_MS,
    refetchOnMountOrArgChange: true,
  });

  const start = React.useCallback(async () => {
    if (taskId || isStarting) return;

    setIsStarting(true);
    setLastStatus(null);
    onLog('success', `Syncing ${label}...`);

    const result = await startSync();
    setIsStarting(false);

    if (result.error || !result.data?.task_id) {
      onLog('error', `Syncing ${label} Failed!`);
      onSettled('error', errorMessage);
      return;
    }

    setTaskId(result.data.task_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, isStarting, startSync, label, errorMessage, onLog, onSettled]);

  const lastLoggedMessage = React.useRef<string | null>(null);

  // One log line per poll response, skipping repeats of the message already showing
  React.useEffect(() => {
    if (!taskId || !status) return;

    if (!isTerminalState(status.state)) {
      if (status.message && status.message !== lastLoggedMessage.current) {
        lastLoggedMessage.current = status.message;
        onLog('success', status.message);
      }
      return;
    }

    // Terminal - keep the last frame for display and unsubscribe to stop polling
    setLastStatus(status);
    setTaskId(null);
    lastLoggedMessage.current = null;

    if (status.state === 'SUCCESS') {
      onLog('success', `Syncing ${label} Complete!`);
      onSettled('success', successMessage);
    } else {
      onLog('error', status.error || `Syncing ${label} Failed!`);
      onSettled('error', status.error || errorMessage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, status]);

  // A failed status request would otherwise poll forever
  React.useEffect(() => {
    if (!taskId || !pollError) return;

    setTaskId(null);
    lastLoggedMessage.current = null;
    onLog('error', `Lost connection to the ${label} sync task`);
    onSettled('error', errorMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, pollError]);

  const isSyncing = isStarting || !!taskId;
  const currentStatus = status ?? lastStatus;

  return {
    start,
    isSyncing,
    percent: isSyncing ? getPercent(status) : 0,
    state: currentStatus?.state,
    message: currentStatus?.message,
  };
}

export default useMyobTaskSync;
